import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/classname';

export interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}

export const AutocompleteInput = ({
  value,
  onChange,
  onSubmit,
  suggestions,
  placeholder = 'Type to search...',
  className,
  'aria-label': ariaLabel,
}: AutocompleteInputProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [lastValue, setLastValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions (case-insensitive prefix match)
  const filteredSuggestions = suggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(value.toLowerCase())
  );

  // Reset selected index when value changes (derived state pattern)
  if (lastValue !== value) {
    setLastValue(value);
    if (selectedIndex !== -1) {
      setSelectedIndex(-1);
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen || filteredSuggestions.length === 0) {
      // Enter key when dropdown closed
      if (e.key === 'Enter') {
        onSubmit(value);
        setIsDropdownOpen(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredSuggestions.length);
        break;

      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev <= 0 ? filteredSuggestions.length - 1 : prev - 1
        );
        break;

      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          onSubmit(filteredSuggestions[selectedIndex]);
        } else {
          onSubmit(value);
        }
        setIsDropdownOpen(false);
        break;

      case 'Tab':
        if (filteredSuggestions.length > 0) {
          e.preventDefault();
          onSubmit(filteredSuggestions[0]);
          setIsDropdownOpen(false);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Handle focus
  const handleFocus = () => {
    setIsDropdownOpen(true);
    setSelectedIndex(-1);
  };

  // Handle blur with delay to allow click events
  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    onSubmit(suggestion);
    setIsDropdownOpen(false);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        aria-label={ariaLabel}
        aria-expanded={isDropdownOpen}
        aria-controls="autocomplete-dropdown"
        role="textbox"
      />

      {isDropdownOpen && filteredSuggestions.length > 0 && (
        <div
          id="autocomplete-dropdown"
          ref={dropdownRef}
          role="listbox"
          className="bg-chariot-background-secondary border-chariot-stroke absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded border shadow-lg"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSuggestionClick(suggestion)}
              className={cn(
                'cursor-pointer px-2 py-1 text-[9px]',
                index === selectedIndex
                  ? 'bg-blue-500/10 text-blue-600'
                  : 'text-chariot-text-primary hover:bg-chariot-background-tertiary'
              )}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
